import './Link.css';
import { useState, useRef, useEffect } from 'react';
import { MdDragIndicator, MdLock } from 'react-icons/md';
import { TextField, IconButton, Typography, Button, useMediaQuery } from '@mui/material';
import { motion, useMotionValue, animate, PanInfo } from 'framer-motion';
import type { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { socialMediaOptions, SocialMediaOption } from '../../IconPicker/Options';
import { BiTrash } from 'react-icons/bi';
import IconPicker from '../../IconPicker/IconPicker';
import { evaluateGuard } from './linkGuard';
import { ProfileLink } from '../../../context/ProfileContext';
import { useTranslation } from 'react-i18next';
import SafeLinkGuardIcon from '../../SafeLinkGuardIcon/SafeLinkGuardIcon';
import { GUARD_STATE_COLOR, SWIPE_DELETE_WIDTH, SWIPE_GAP, SWIPE_OPEN_THRESHOLD, SWIPE_VELOCITY_THRESHOLD } from 'utils/constants';
import { GuardState } from 'utils/enums';

const GUARD_DEBOUNCE_MS = 400;

const resolveOption = (icon_id: number | null, social_name: string): SocialMediaOption | null => {
    if (!icon_id && !social_name) return null;
    return socialMediaOptions.find(o => o.id === icon_id || o.label === social_name) ?? null;
};

interface LinkProps {
    link: ProfileLink;
    allLinks: ProfileLink[];
    onHandleDnd: DraggableProvidedDragHandleProps | null | undefined;
    onHandleDeleteLink: (id: number) => void;
    onHandleEditLink: (
        type: 1 | 2,
        data: { id: number; value: string; icon?: string; icon_id?: number }
    ) => void;
}

const Link = ({ link, allLinks, onHandleDnd, onHandleDeleteLink, onHandleEditLink }: LinkProps) => {
    const { t } = useTranslation();
    const socialUrl = link.social_url ?? '';
    const isTouchOnly = useMediaQuery('(hover: none) and (pointer: coarse)');

    const [valueSelected, setSelectedValue] = useState<SocialMediaOption | null>(
        () => resolveOption(link.icon_id, link.social_name)
    );
    const [isHovered, setIsHovered] = useState(false);
    const swipeX = useMotionValue(0);

    // Debounced URL for guard — avoids evaluating on every keystroke
    const [evalUrl, setEvalUrl] = useState(socialUrl);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
    }, []);

    const guard = evaluateGuard(evalUrl, valueSelected, allLinks, link.id_link);

    const handleChangeSocialUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        onHandleEditLink(1, { id: link.id_link, value: val });
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setEvalUrl(val.trim()), GUARD_DEBOUNCE_MS);
    };

    const handleBlurSocialUrl = () => {
        const trimmed = socialUrl.trim();
        if (trimmed !== socialUrl) {
            onHandleEditLink(1, { id: link.id_link, value: trimmed });
            if (debounceRef.current) clearTimeout(debounceRef.current);
            setEvalUrl(trimmed);
        }
    };

    const handleSelectIcon = (option: SocialMediaOption) => {
        setSelectedValue(option);
        onHandleEditLink(2, { id: link.id_link, value: option.label, icon: option.icon, icon_id: option.id });
    };

    const handleCleanUrl = (cleanedUrl: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setEvalUrl(cleanedUrl);
        onHandleEditLink(1, { id: link.id_link, value: cleanedUrl });
    };

    const handleSwipeDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
        const shouldOpen = info.offset.x < -SWIPE_OPEN_THRESHOLD || info.velocity.x < -SWIPE_VELOCITY_THRESHOLD;
        const target = shouldOpen ? -(SWIPE_DELETE_WIDTH + SWIPE_GAP) : 0;
        animate(swipeX, target, { type: 'spring', stiffness: 400, damping: 40 });
    };

    // Touch synthesizes `mouseenter` on tap but rarely fires the matching
    // `mouseleave` (no pointer to "leave" with), so isHovered can get stuck
    // true — leaving the card visually highlighted after the user moved on.
    // Touch devices use swipe-to-delete instead, so hover has no job there.
    const handleMouseEnter = () => { if (!isTouchOnly) setIsHovered(true); };
    const handleMouseLeave = () => { if (!isTouchOnly) setIsHovered(false); };

    return (
        <div className="editable-link-swipe-wrapper">
            <motion.div
                className="editable-link-swipe-row"
                style={{ x: swipeX }}
                drag={isTouchOnly ? 'x' : false}
                dragConstraints={{ left: -(SWIPE_DELETE_WIDTH + SWIPE_GAP), right: 0 }}
                dragElastic={0.01}
                dragMomentum={false}
                dragDirectionLock
                onDragEnd={handleSwipeDragEnd}
            >
                <div
                    className={`editable-link-item${isHovered ? ' editable-link-item--hovered' : ''}`}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
            {/* Main row */}
            <div className="editable-link-main">
                <div className="editable-link-dnd-handle" {...onHandleDnd}>
                    <MdDragIndicator fontSize={22} />
                </div>

                <TextField
                    name="link"
                    className="editable-link-textfield"
                    variant="outlined"
                    size="small"
                    value={socialUrl}
                    placeholder={t('profile.links.link.txt-link')}
                    error={guard.state === GuardState.ERROR}
                    onChange={handleChangeSocialUrl}
                    onBlur={handleBlurSocialUrl}
                />

                <IconPicker value={valueSelected} onChange={handleSelectIcon} />

                <IconButton
                    className={`editable-link-delete${isHovered ? ' editable-link-delete--visible' : ''}`}
                    aria-label="delete link"
                    size="medium"
                    onClick={() => onHandleDeleteLink(link.id_link)}
                >
                    <BiTrash fontSize={20} />
                </IconButton>
            </div>

            {guard.state !== GuardState.IDLE && (
                <div className="editable-link-guard">
                    <div className="editable-link-guard-status">
                        <SafeLinkGuardIcon
                            variant="lite"
                            size={16}
                            color={GUARD_STATE_COLOR[guard.state]}
                        />

                        {guard.state === GuardState.VALID && (
                            <Typography variant="caption" sx={{ color: 'success.main' }}>
                                {t('profile.links.link.guard.valid')}
                            </Typography>
                        )}
                        {guard.state === GuardState.WARNING && (
                            <Typography variant="caption" sx={{ color: 'warning.main' }}>
                                {t(`profile.links.link.${guard.key}`, {
                                    selected: valueSelected?.label ?? '',
                                    platform: guard.switchTo?.label ?? '',
                                })}
                            </Typography>
                        )}
                        {guard.state === GuardState.ERROR && (
                            <Typography variant="caption" className="editable-link-error">
                                {t(`profile.links.link.${guard.key}`)}
                            </Typography>
                        )}
                    </div>

                    {/* Actions — separate line, aligned with the message text */}
                    {guard.switchTo && (
                        <Button
                            size="small"
                            variant="text"
                            className="editable-link-guard-action"
                            onClick={() => handleSelectIcon(guard.switchTo!)}
                        >
                            {t('profile.links.link.guard.mismatch-switch', { platform: guard.switchTo.label })}
                        </Button>
                    )}
                    {guard.cleanedUrl && (
                        <Button
                            size="small"
                            variant="text"
                            className="editable-link-guard-action"
                            onClick={() => handleCleanUrl(guard.cleanedUrl!)}
                        >
                            {t('profile.links.link.guard.tracking-clean')}
                        </Button>
                    )}
                    {/* PRO: deep link scan — locked placeholder, code path ready */}
                    {guard.showPro && (
                        <Button
                            size="small"
                            variant="text"
                            disabled
                            startIcon={<MdLock fontSize={11} />}
                            className="editable-link-guard-pro"
                        >
                            {t('profile.links.link.guard.pro-scan')}
                        </Button>
                    )}
                </div>
            )}
                </div>

                <button
                    type="button"
                    className="editable-link-swipe-delete"
                    aria-label="delete link"
                    onClick={() => onHandleDeleteLink(link.id_link)}
                >
                    <BiTrash size={22} />
                </button>
            </motion.div>
        </div>
    );
};

export default Link;
